import { envConfig } from '@/shared/config/env'
import { logtoStorage } from '@/utils/storage/logto'
import { usePostHog } from '@/shared/composables/usePostHog'

const { posthog } = usePostHog()

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4 || 4)) % 4)
  try {
    return decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    )
  } catch (error) {
    console.error('[UserTokenScopes] Base64 URL 解码失败:', error)
    posthog.capture('jwt_parse_error', {
      error: String(error),
      tokenType: 'access_token',
      operation: 'base64_decode',
    })
    return ''
  }
}

const parseJwtPayload = (rawToken: string): Record<string, unknown> | null => {
  const segments = rawToken.split('.')
  if (segments.length < 2) return null
  const payloadText = decodeBase64Url(segments[1] || '')
  if (!payloadText) return null
  try {
    const parsed = JSON.parse(payloadText)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    console.error('[UserTokenScopes] JWT Payload 解析失败:', error)
    posthog.capture('jwt_parse_error', {
      error: String(error),
      tokenType: 'access_token',
      operation: 'json_parse',
    })
    return null
  }
}

const appendScopes = (bucket: Set<string>, value: unknown) => {
  if (typeof value === 'string') {
    value
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => bucket.add(item))
    return
  }
  if (Array.isArray(value)) {
    value
      .map((item) => String(item || '').trim())
      .filter(Boolean)
      .forEach((item) => bucket.add(item))
  }
}

const parseScopesFromLogtoAccessTokenCache = () => {
  const scopeSet = new Set<string>()
  const raw = logtoStorage.getAccessTokenCacheRaw()
  if (!raw) return scopeSet

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    Object.values(parsed || {}).forEach((entry) => {
      if (!entry || typeof entry !== 'object') return
      appendScopes(scopeSet, (entry as { scope?: unknown }).scope)
    })
  } catch (error) {
    console.error('[UserTokenScopes] 解析 Logto 缓存 scope 失败:', error)
    posthog.capture('storage_error', {
      operation: 'parse_logto_cache',
      storageType: 'localStorage',
      error: String(error),
    })
    // Ignore parse failure and keep empty fallback.
  }

  return scopeSet
}

export const parseTokenScopes = (rawToken: string | null) => {
  const scopeSet = new Set<string>()
  if (!rawToken) return scopeSet
  const payload = parseJwtPayload(rawToken)
  if (payload) {
    appendScopes(scopeSet, payload.scope)
    appendScopes(scopeSet, payload.scp)
  }

  if (scopeSet.size === 0) {
    const cacheScopes = parseScopesFromLogtoAccessTokenCache()
    cacheScopes.forEach((scope) => scopeSet.add(scope))
  }

  return scopeSet
}

const parseAccessTokenCache = () => {
  const raw = logtoStorage.getAccessTokenCacheRaw()
  if (!raw) return null

  try {
    return JSON.parse(raw) as Record<string, { expiresAt?: number; token?: string } | undefined>
  } catch (error) {
    console.error('[UserTokenScopes] 解析访问令牌缓存失败:', error)
    posthog.capture('storage_error', {
      operation: 'parse_token_cache',
      storageType: 'localStorage',
      error: String(error),
    })
    return null
  }
}

const getDefaultTokenCacheKey = () => `@${envConfig.logtoResourceApi}`

export const getTokenExpiryAtMs = (rawToken: string | null) => {
  if (!rawToken) return null

  const payload = parseJwtPayload(rawToken)
  if (typeof payload?.exp === 'number' && Number.isFinite(payload.exp)) {
    return payload.exp * 1000
  }

  const cache = parseAccessTokenCache()
  if (!cache) return null

  const matchedEntry =
    Object.values(cache).find((entry) => entry?.token === rawToken) ?? cache[getDefaultTokenCacheKey()]

  if (typeof matchedEntry?.expiresAt === 'number' && Number.isFinite(matchedEntry.expiresAt)) {
    return matchedEntry.expiresAt * 1000
  }

  return null
}
